import type { ColumnFilterState } from '../../types/table';
import type { PolicyResearchTableColumnSetting } from '../../types/chat';
import type { ChatCategoryContractsSort, ChatCategoryFilters } from '../../stores/ChatCategoryStore';
import { readTenantFilters, writeTenantFilters } from './filtersTenantStorage';

// localStorage blobs for the research pages' top filters, table column filters,
// table sort and (policies only) column settings, one key per page. Both pages share ChatCategoryStore.filters, so
// each saves only its own fields. Stored per tenant and user, see filtersTenantStorage.
export const POLICIES_FILTERS_STORAGE_KEY = 'filters:policies';
export const CONTRACTS_FILTERS_STORAGE_KEY = 'filters:contracts';

export const POLICIES_FILTER_FIELDS = ['policySearchQuery', 'state', 'payer', 'lineOfBusiness'] as const;
export const CONTRACTS_FILTER_FIELDS = ['contractSearchQuery', 'state', 'payer', 'lineOfBusiness'] as const;

export type ResearchFilterField = keyof ChatCategoryFilters;

export interface PersistedResearchFilters {
  filters: Partial<ChatCategoryFilters>;
  columnFilters: Record<string, ColumnFilterState>;
  /** by: '' = default order (title asc). */
  sort: ChatCategoryContractsSort;
  /** Column visibility + pin settings; null = never saved (use the defaults). Only the policies page has them. */
  columnSettings?: PolicyResearchTableColumnSetting[] | null;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const isColumnFilterState = (value: unknown): value is ColumnFilterState =>
  isPlainObject(value) &&
  (value.operator === 'and' || value.operator === 'or' || value.operator === 'isnot') &&
  Array.isArray(value.values) &&
  value.values.every((v) => typeof v === 'string');

const parseColumnFilters = (value: unknown): Record<string, ColumnFilterState> => {
  if (!isPlainObject(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, ColumnFilterState] => isColumnFilterState(entry[1]) && entry[1].values.length > 0,
    ),
  );
};

const parseColumnSettings = (value: unknown): PolicyResearchTableColumnSetting[] | null =>
  Array.isArray(value)
    ? value.filter(
        (setting): setting is PolicyResearchTableColumnSetting =>
          isPlainObject(setting) &&
          typeof setting.id === 'string' &&
          typeof setting.visible === 'boolean' &&
          typeof setting.required === 'boolean',
      )
    : null;

const parseSort = (value: unknown): ChatCategoryContractsSort =>
  isPlainObject(value) && typeof value.by === 'string' && (value.direction === 'asc' || value.direction === 'desc')
    ? { by: value.by, direction: value.direction }
    : { by: '', direction: 'asc' };

// The blob is flat: each field under its ChatCategoryFilters name, plus `columnFilters`, `sort`
// and, when saved, `columnSettings`.
export const loadPersistedResearchFilters = (
  key: string,
  scope: string | null,
  fields: readonly ResearchFilterField[],
): PersistedResearchFilters => {
  const saved = readTenantFilters(key, scope);
  const blob: Record<string, unknown> = isPlainObject(saved) ? saved : {};

  return {
    filters: Object.fromEntries(fields.map((field) => [field, asString(blob[field])])),
    columnFilters: parseColumnFilters(blob.columnFilters),
    sort: parseSort(blob.sort),
    columnSettings: parseColumnSettings(blob.columnSettings),
  };
};

export const savePersistedResearchFilters = (
  key: string,
  scope: string | null,
  persisted: PersistedResearchFilters,
): void => {
  writeTenantFilters(key, scope, {
    ...persisted.filters,
    columnFilters: persisted.columnFilters,
    sort: persisted.sort,
    ...(persisted.columnSettings ? { columnSettings: persisted.columnSettings } : {}),
  });
};
